import {put, call, all} from 'redux-saga/effects';
import User from "../../entities/user";
import * as Actions from "../actions/userActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type UserAction = {
    userId: string;
    user: User;
    users: User[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneUser(action: UserAction) {
	const session = tabSession.captureGeneration();
	try {
		const user: User = yield call(api.findOne, 'user', action.userId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.USER_FETCH_ONE_SUCCESS, userId: action.userId, user, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.USER_FETCH_ONE_FAILURE, userId: action.userId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {UserAction} action
 */
export function* saveUser(action: UserAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ USER_SAVE saga', action.user?.id);

		if (action.user === undefined || action.user === null) {
			console.error('saveUser received a null');
			throw new Error('saveUser received a null');
		}

		if (!(action.user instanceof User)) {
			action.user = new User(action.user);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.user.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.user);
			const addResult = yield call(idb.add, 'user', action.user);
			if (addResult.isErr()) {
				console.error(`⮂ saveUser: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertUserSave(action.user, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.USER_SAVE_FAILURE, user: action.user, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.USER_SAVE, user: action.user, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertUserSave(action.user, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.USER_SAVE_FAILURE, user: action.user, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {UserAction} action
 * @param {User[]} action.users
 */
export function* saveManyUsers(action: UserAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ USER_SAVE_MANY saga', action.users.map(user => user.id).join(','));

        if (!Array.isArray(action.users)) {
            console.error('saveManyUsers expected an array, received', action.users);
            throw new Error('saveManyUsers expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveUser), under these queue keys
		const dispatchedAddedOns: number[] = action.users.map(user => user?.addedOn);
		const revertAll = () => action.users.map((user, i) =>
			put(Actions.revertUserSave(user, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: User[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.users.length; i++) {
				const user = action.users[i];
				user.persisted = false;
				ensureOperationId(user);
				toSave.push(user);
			}

			const addResult = yield call(idb.add, 'user', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyUsers: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.USER_SAVE_MANY_FAILURE, users: action.users, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.USER_SAVE_MANY, users: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.USER_SAVE_MANY_FAILURE, users: action.users, status: 500, err});

			console.error(`Error saving ${action.users.length} users to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {UserAction} action
 */
export function* removeUser(action: UserAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.user === undefined || action.user === null || !(action.user instanceof User)) {
			console.error('removeUser expected type User, received', action.user);
			reportQueued(false);
			throw new Error('removeUser expected type User');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.user.status = 'D';
		action.user.addedOn = newHRTimestamp();
		action.user.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.user);
		const addResult = yield call(idb.add, 'user', action.user);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeUser: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.USER_REMOVE_FAILURE, user: action.user, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.USER_REMOVE, user: action.user});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.USER_REMOVE_FAILURE, user: action.user, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this User.
	*
	* @param {UserAction} action
	*/
export function* restoreUser(action: UserAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreUser expected getCurrentUserId() to return a value');
        return;
    }

	if (action.user === undefined || action.user === null) {
        console.error('restoreUser expected a User, received null');
        throw new Error('restoreUser expected a User, received null');
    }
    else if (!(action.user instanceof User)) action.user = new User(action.user); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.user.status = 'U';
		action.user.addedOn = newHRTimestamp();
		action.user.addedBy = userId;
		action.user.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.user);
		const addResult = yield call(idb.add, 'user', action.user);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreUser: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.USER_RESTORE_FAILURE, user: action.user, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.USER_RESTORE, user: action.user});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.USER_RESTORE_FAILURE, user: action.user, status: 500, err});

		console.error('Error restoring from database', err);
	}
}